import {
  cloneElement,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, RefreshCw } from 'lucide-react';
import type {
  CampaignAudiencePreview,
  CampaignDeliveryStatus,
  CampaignDetails,
  CampaignDraftInput,
  CampaignMessageInput,
  CampaignRecipient,
  CampaignReconcileInput,
} from '@lms/shared/types';
import { apiClient } from '../lib/api-client';
import { useLanguage } from '../lib/LanguageContext';
import { useAuthStore } from '../store/auth';
import { usePrograms } from '../hooks/usePrograms';
import { campaignCopy as c } from '../copy/campaign-copy';
import { CampaignOverviewPanel } from '../components/CampaignOverviewPanel';
import './email-campaigns.css';

const queries = ['email-campaigns'] as const;
const deliveryStatuses = Object.keys(c.deliveryStatuses) as CampaignDeliveryStatus[];
function Field({ label, children }: { label: string; children: ReactElement<{ id?: string }> }) {
  const id = useId();
  return (
    <div className="campaign-field">
      <label htmlFor={id}>{label}</label>
      {cloneElement(children, { id })}
    </div>
  );
}
function ErrorMessage({ error }: { error: unknown }) {
  const { t } = useLanguage();
  return error ? (
    <p role="alert" className="campaign-error">
      {error instanceof Error ? error.message : t(c.error)}
    </p>
  ) : null;
}
function dateWib(value: string | null, language: string): string {
  return value
    ? new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'id-ID', {
        timeZone: 'Asia/Jakarta',
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';
}
function todayWib(): string {
  return new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 10);
}

export function EmailCampaigns() {
  const { t } = useLanguage();
  const user = useAuthStore(state => state.user);
  const { id } = useParams();
  const allowed = user?.roles?.some(role =>
    ['ADMIN', 'SUPERADMIN', 'SUPER_ADMIN'].includes(
      (typeof role === 'string' ? role : role.role.name).toUpperCase()
    )
  );
  if (!allowed) return <p role="alert">{t(c.denied)}</p>;
  return (
    <main className="campaign-workspace">
      {id ? <CampaignWorkspace id={id} /> : <CampaignIndex />}
    </main>
  );
}

function CampaignIndex() {
  const { t, language } = useLanguage();
  const [page, setPage] = useState(1);
  const result = useQuery({
    queryKey: [...queries, 'list', page],
    queryFn: () => apiClient.emailCampaigns.list(page),
    refetchInterval: 30000,
  });
  return (
    <>
      <header className="campaign-header">
        <div>
          <h1>{t(c.title)}</h1>
          <p>{t(c.description)}</p>
        </div>
        <Link className="campaign-primary" to="/email-campaigns/new">
          <Plus size={20} />
          {t(c.create)}
        </Link>
      </header>
      <CampaignOverviewPanel />
      <ErrorMessage error={result.error} />
      {result.isPending ? (
        <p role="status">{t(c.loading)}</p>
      ) : !result.data?.items.length ? (
        <p>{t(c.empty)}</p>
      ) : (
        <div className="campaign-directory">
          {result.data.items.map(item => (
            <article key={item.id}>
              <div>
                <Link to={`/email-campaigns/${item.id}`} className="campaign-title-link">
                  {item.name}
                </Link>
                <p>
                  {item.program.code} · {item.program.name}
                </p>
                <p>{item.subject}</p>
              </div>
              <div>
                <span className="campaign-badge">{t(c.draftStatuses[item.status])}</span>
                <p>{dateWib(item.scheduledAt, language)}</p>
                <p className="tabular-nums">
                  {item.totalRecipients} {t(c.email)}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
      <div className="campaign-actions">
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
          {t(c.previous)}
        </button>
        <span>{page}</span>
        <button
          disabled={!result.data || page * 25 >= result.data.total}
          onClick={() => setPage(page + 1)}
        >
          {t(c.next)}
        </button>
        <button onClick={() => void result.refetch()} aria-label={t(c.retry)}>
          <RefreshCw size={20} />
        </button>
      </div>
    </>
  );
}

function CampaignWorkspace({ id }: { id: string }) {
  const { t } = useLanguage();
  const isNew = id === 'new';
  const result = useQuery({
    queryKey: [...queries, id],
    queryFn: () => apiClient.emailCampaigns.get(id),
    enabled: !isNew,
    refetchInterval: query => (query.state.data?.status === 'DRAFT' ? false : 5000),
  });
  const configuration = useQuery({
    queryKey: [...queries, 'configuration'],
    queryFn: apiClient.emailCampaigns.configuration,
  });
  return (
    <>
      <Link to="/email-campaigns" className="campaign-back">
        {t(c.back)}
      </Link>
      <header className="campaign-header">
        <div>
          <h1>{result.data?.name ?? t(c.create)}</h1>
          <p>{t(c.description)}</p>
        </div>
        <Mail aria-hidden="true" size={28} />
      </header>
      <ErrorMessage error={result.error ?? configuration.error} />
      {!configuration.data?.enabled && (
        <p className="campaign-note" role="status">
          {t(c.disabled)}
        </p>
      )}
      {!isNew && result.isPending ? (
        <p role="status">{t(c.loading)}</p>
      ) : isNew || result.data?.status === 'DRAFT' ? (
        <CampaignEditor
          key={`${id}-${result.data?.revision ?? 0}`}
          initial={result.data}
          enabled={configuration.data?.enabled ?? false}
        />
      ) : result.data ? (
        <CampaignReport campaign={result.data} />
      ) : (
        <button onClick={() => void result.refetch()}>{t(c.retry)}</button>
      )}
      {!configuration.data?.webhookConfigured && (
        <p className="campaign-note">{t(c.webhookMissing)}</p>
      )}
    </>
  );
}

function CampaignEditor({ initial, enabled }: { initial?: CampaignDetails; enabled: boolean }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [form, setForm] = useState<CampaignDraftInput>({
    name: initial?.name ?? '',
    subject: initial?.subject ?? '',
    body: initial?.body ?? '',
    programId: initial?.programId ?? '',
    gender: initial?.gender ?? undefined,
    registrationStatus: initial?.registrationStatus ?? 'APPROVED',
    actionLabel: initial?.actionLabel ?? '',
    actionUrl: initial?.actionUrl ?? '',
    revision: initial?.revision,
  });
  const [dirty, setDirty] = useState(!initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [notice, setNotice] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const [audience, setAudience] = useState<CampaignAudiencePreview>();
  const [preview, setPreview] = useState<{ subject: string; html: string }>();
  const [templateId, setTemplateId] = useState('');
  const [when, setWhen] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const programs = usePrograms({ limit: 100, search: programSearch || undefined });
  const templates = useQuery({
    queryKey: [...queries, 'templates'],
    queryFn: apiClient.emailCampaigns.templates,
  });
  const change = <K extends keyof CampaignDraftInput>(key: K, value: CampaignDraftInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    setPreview(undefined);
    setNotice('');
    setConfirmed(false);
    if (['programId', 'gender', 'registrationStatus'].includes(key)) setAudience(undefined);
  };
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    setNotice('');
    try {
      await work();
    } catch (failure: unknown) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };
  const content = (): CampaignMessageInput => ({
    name: form.name,
    subject: form.subject,
    body: form.body,
    ...(form.actionLabel ? { actionLabel: form.actionLabel } : {}),
    ...(form.actionUrl ? { actionUrl: form.actionUrl } : {}),
  });
  const save = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const input = {
        ...content(),
        programId: form.programId,
        gender: form.gender,
        registrationStatus: form.registrationStatus,
        revision: form.revision,
      };
      const saved = initial
        ? await apiClient.emailCampaigns.update(initial.id, input)
        : await apiClient.emailCampaigns.create(input);
      setForm(prev => ({ ...prev, revision: saved.revision }));
      setDirty(false);
      setNotice(t(c.saved));
      await client.invalidateQueries({ queryKey: queries });
      if (!initial) void navigate(`/email-campaigns/${saved.id}`, { replace: true });
    });
  };
  const applyTemplate = (value: string) => {
    setTemplateId(value);
    const preset =
      value === 'announcement'
        ? { subject: t(c.announcementSubject), body: t(c.announcementBody) }
        : value === 'reminder'
          ? { subject: t(c.reminderSubject), body: t(c.reminderBody) }
          : templates.data?.find(item => item.id === value);
    if (preset) {
      setForm(prev => ({
        ...prev,
        subject: preset.subject,
        body: preset.body,
        actionLabel: 'actionLabel' in preset ? (preset.actionLabel ?? '') : '',
        actionUrl: 'actionUrl' in preset ? (preset.actionUrl ?? '') : '',
      }));
      setDirty(true);
      setPreview(undefined);
      setConfirmed(false);
    }
  };
  return (
    <>
      <ErrorMessage error={error ?? programs.error ?? templates.error} />
      {notice && (
        <p role="status" className="campaign-note">
          {notice}
        </p>
      )}
      <form onSubmit={save} className="campaign-compose">
        <section>
          <h2>{t(c.chooseAudience)}</h2>
          <Field label={t(c.programSearch)}>
            <input value={programSearch} onChange={event => setProgramSearch(event.target.value)} />
          </Field>
          <Field label={t(c.program)}>
            <select
              required
              value={form.programId}
              onChange={event => change('programId', event.target.value)}
            >
              <option value="">{t(c.programPlaceholder)}</option>
              {initial &&
                !programs.data?.items.some(program => program.id === initial.programId) && (
                  <option value={initial.programId}>{initial.program.name}</option>
                )}
              {programs.data?.items.map(program => (
                <option value={program.id} key={program.id}>
                  {program.code} · {program.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t(c.gender)}>
            <select
              value={form.gender ?? ''}
              onChange={event =>
                change(
                  'gender',
                  event.target.value === '' ? undefined : (event.target.value as 'MALE' | 'FEMALE')
                )
              }
            >
              <option value="">{t(c.allGender)}</option>
              <option value="MALE">{t(c.male)}</option>
              <option value="FEMALE">{t(c.female)}</option>
            </select>
          </Field>
          <Field label={t(c.registrationStatus)}>
            <select
              value={form.registrationStatus}
              onChange={event =>
                change(
                  'registrationStatus',
                  event.target.value as CampaignDraftInput['registrationStatus']
                )
              }
            >
              {Object.entries(c.registrationStatuses).map(([value, label]) => (
                <option key={value} value={value}>
                  {t(label)}
                </option>
              ))}
            </select>
          </Field>
          <p>{t(c.consentHint)}</p>
          <button
            type="button"
            disabled={busy || !form.programId}
            onClick={() =>
              void run(async () => {
                setAudience(
                  await apiClient.emailCampaigns.audience({
                    programId: form.programId,
                    gender: form.gender,
                    registrationStatus: form.registrationStatus,
                  })
                );
              })
            }
          >
            {t(c.checkAudience)}
          </button>
          {audience && (
            <div aria-live="polite">
              <p className="campaign-count">
                {audience.eligible} {t(c.eligible)}
              </p>
              <p>
                {audience.total} {t(c.email)} · {audience.blocked}{' '}
                {t(c.deliveryStatuses.BLACKLISTED)} · {audience.skipped} {t(c.excluded)}
              </p>
              <p>{t(c.sample)}</p>
              <ul className="campaign-sample">
                {audience.sample.map(row => (
                  <li key={row.email}>
                    <strong>{row.name}</strong>
                    <span>{row.email}</span>
                    <span>
                      {t(c.deliveryStatuses[row.status])}
                      {row.reason ? ` · ${row.reason}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
        <section>
          <h2>{t(c.message)}</h2>
          <Field label={t(c.template)}>
            <select value={templateId} onChange={event => applyTemplate(event.target.value)}>
              <option value="">{t(c.custom)}</option>
              <option value="announcement">{t(c.announcement)}</option>
              <option value="reminder">{t(c.reminder)}</option>
              {templates.data?.map(item => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t(c.name)}>
            <input
              required
              maxLength={120}
              value={form.name}
              onChange={event => change('name', event.target.value)}
            />
          </Field>
          <Field label={t(c.subject)}>
            <input
              required
              maxLength={200}
              value={form.subject}
              onChange={event => change('subject', event.target.value)}
            />
          </Field>
          <Field label={t(c.body)}>
            <textarea
              required
              maxLength={30000}
              rows={10}
              aria-describedby="campaign-variables"
              value={form.body}
              onChange={event => change('body', event.target.value)}
            />
          </Field>
          <p id="campaign-variables">{t(c.variables)}</p>
          <Field label={t(c.actionLabel)}>
            <input
              maxLength={80}
              value={form.actionLabel ?? ''}
              onChange={event => change('actionLabel', event.target.value)}
            />
          </Field>
          <Field label={t(c.actionUrl)}>
            <input
              type="url"
              pattern="https://.*"
              value={form.actionUrl ?? ''}
              onChange={event => change('actionUrl', event.target.value)}
            />
          </Field>
          <div className="campaign-actions">
            <button type="submit" className="campaign-primary" disabled={busy}>
              {t(c.save)}
            </button>
            <button
              type="button"
              disabled={busy || !form.name.trim() || !form.subject.trim() || !form.body.trim()}
              onClick={() =>
                void run(async () => {
                  await apiClient.emailCampaigns.saveTemplate(content());
                  await client.invalidateQueries({ queryKey: [...queries, 'templates'] });
                  setNotice(t(c.saved));
                })
              }
            >
              {t(c.saveTemplate)}
            </button>
            {templates.data?.some(item => item.id === templateId) && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await apiClient.emailCampaigns.saveTemplate(content(), templateId);
                    await client.invalidateQueries({ queryKey: [...queries, 'templates'] });
                    setNotice(t(c.saved));
                  })
                }
              >
                {t(c.updateTemplate)}
              </button>
            )}
          </div>
        </section>
      </form>
      <section className="campaign-review">
        <h2>{t(c.preview)}</h2>
        {dirty && <p>{t(c.saveFirst)}</p>}
        <button
          disabled={busy || dirty || !initial}
          onClick={() =>
            void run(async () => {
              setPreview(await apiClient.emailCampaigns.preview(initial!.id));
            })
          }
        >
          {t(c.preview)}
        </button>
        {preview && (
          <>
            <h3>{preview.subject}</h3>
            <iframe title={t(c.preview)} sandbox="" srcDoc={preview.html} />
          </>
        )}
      </section>
      <section className="campaign-review">
        <h2>{t(c.scheduleHeading)}</h2>
        <p>{t(c.frozen)}</p>
        <Field label={t(c.when)}>
          <input
            type="datetime-local"
            value={when}
            onChange={event => setWhen(event.target.value)}
          />
        </Field>
        <label className="campaign-checkbox">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={event => setConfirmed(event.target.checked)}
          />
          <span>{t(c.confirm)}</span>
        </label>
        <button
          className="campaign-primary"
          disabled={
            busy ||
            !enabled ||
            dirty ||
            !initial ||
            !preview ||
            !audience?.eligible ||
            !when ||
            !confirmed
          }
          onClick={() =>
            void run(async () => {
              await apiClient.emailCampaigns.schedule(initial!.id, {
                scheduledAt: new Date(`${when}:00+07:00`).toISOString(),
                revision: form.revision!,
                expectedRecipients: audience!.total,
                educationalPurposeConfirmed: true,
              });
              setNotice(t(c.scheduled));
              await client.invalidateQueries({ queryKey: queries });
            })
          }
        >
          {t(c.schedule)}
        </button>
      </section>
    </>
  );
}

function CampaignReport({ campaign }: { campaign: CampaignDetails }) {
  const { t, language } = useLanguage();
  const client = useQueryClient();
  const [status, setStatus] = useState<CampaignDeliveryStatus | ''>('');
  const [day, setDay] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [selected, setSelected] = useState<CampaignRecipient>();
  const [reason, setReason] = useState('');
  const [reconcileStatus, setReconcileStatus] =
    useState<CampaignReconcileInput['status']>('FAILED');
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (selected) dialog.current?.showModal();
    else dialog.current?.close();
  }, [selected]);
  const recipients = useQuery({
    queryKey: [...queries, campaign.id, 'recipients', status, day, search, page],
    queryFn: () =>
      apiClient.emailCampaigns.recipients(campaign.id, {
        page,
        status: status || undefined,
        day: day || undefined,
        search: search || undefined,
      }),
    refetchInterval: 15000,
  });
  const action = async (kind: 'pause' | 'resume' | 'cancel') => {
    if (kind === 'cancel' && !window.confirm(t(c.cancelConfirm))) return;
    setBusy(true);
    setError(undefined);
    try {
      await apiClient.emailCampaigns.action(campaign.id, kind);
      await client.invalidateQueries({ queryKey: queries });
    } catch (failure: unknown) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ErrorMessage error={error ?? recipients.error} />
      <section className="campaign-review">
        <div className="campaign-header">
          <h2>{t(c.progress)}</h2>
          <span className="campaign-badge">{t(c.draftStatuses[campaign.status])}</span>
        </div>
        <p>
          {dateWib(campaign.scheduledAt, language)} · {campaign.program.name}
        </p>
        <p className="campaign-count" aria-live="polite">
          {campaign.processed} {t(c.of)} {campaign.totalRecipients}
        </p>
        <progress
          aria-label={t(c.progress)}
          value={campaign.processed}
          max={Math.max(1, campaign.totalRecipients)}
        />
        <dl className="campaign-metrics">
          {deliveryStatuses.map(value => (
            <div key={value}>
              <dt>{t(c.deliveryStatuses[value])}</dt>
              <dd>{campaign.counts[value] ?? 0}</dd>
            </div>
          ))}
        </dl>
        <p>{t(c.notice)}</p>
        {campaign.lastError && <p className="campaign-error">{campaign.lastError}</p>}
        <div className="campaign-actions">
          {['SCHEDULED', 'RUNNING'].includes(campaign.status) && (
            <button disabled={busy} onClick={() => void action('pause')}>
              {t(c.pause)}
            </button>
          )}
          {campaign.status === 'PAUSED' && (
            <button disabled={busy} onClick={() => void action('resume')}>
              {t(c.resume)}
            </button>
          )}
          {['SCHEDULED', 'RUNNING', 'PAUSED'].includes(campaign.status) && (
            <button disabled={busy} onClick={() => void action('cancel')}>
              {t(c.cancel)}
            </button>
          )}
        </div>
        <p>{t(c.inflight)}</p>
      </section>
      <section>
        <h2>{t(c.recipients)}</h2>
        <div className="campaign-filters">
          <Field label={t(c.search)}>
            <input
              value={search}
              onChange={event => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label={t(c.status)}>
            <select
              value={status}
              onChange={event => {
                setStatus(event.target.value as CampaignDeliveryStatus | '');
                setPage(1);
              }}
            >
              <option value="">{t(c.allStatus)}</option>
              {deliveryStatuses.map(value => (
                <option key={value} value={value}>
                  {t(c.deliveryStatuses[value])}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t(c.day)}>
            <input
              type="date"
              value={day}
              onChange={event => {
                setDay(event.target.value);
                setPage(1);
              }}
            />
          </Field>
        </div>
        <div className="campaign-actions">
          <button
            onClick={() => {
              setDay(todayWib());
              setPage(1);
            }}
          >
            {t(c.today)}
          </button>
          <button
            onClick={() => {
              setDay('');
              setPage(1);
            }}
          >
            {t(c.allDays)}
          </button>
        </div>
        <div className="campaign-table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t(c.email)}</th>
                <th>{t(c.status)}</th>
                <th>{t(c.time)}</th>
                <th>{t(c.reason)}</th>
              </tr>
            </thead>
            <tbody>
              {recipients.data?.items.map(row => (
                <tr key={row.id}>
                  <td data-label={t(c.email)}>
                    <strong>{row.name}</strong>
                    <span>{row.email}</span>
                    <span>NIP: {row.nip ?? '—'}</span>
                  </td>
                  <td data-label={t(c.status)}>{t(c.deliveryStatuses[row.status])}</td>
                  <td data-label={t(c.time)}>{dateWib(row.attemptedAt, language)}</td>
                  <td data-label={t(c.reason)}>
                    <span>{row.reason ?? '—'}</span>
                    <small>
                      {row.source} · {row.messageId ?? '—'}
                    </small>
                    {['ACCEPTED', 'UNKNOWN', 'FAILED', 'DELIVERED'].includes(row.status) && (
                      <button
                        onClick={() => {
                          setSelected(row);
                          setReason('');
                          setReconcileStatus('FAILED');
                        }}
                      >
                        {t(c.reconcile)}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {recipients.isPending ? (
          <p role="status">{t(c.loading)}</p>
        ) : (
          !recipients.data?.items.length && <p>{t(c.noRecipients)}</p>
        )}
        <div className="campaign-actions">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t(c.previous)}
          </button>
          <span>
            {page} · {recipients.data?.total ?? 0}
          </span>
          <button
            disabled={!recipients.data || page * 25 >= recipients.data.total}
            onClick={() => setPage(page + 1)}
          >
            {t(c.next)}
          </button>
        </div>
      </section>
      <dialog
        ref={dialog}
        className="campaign-dialog"
        onCancel={() => setSelected(undefined)}
        onClick={event => {
          if (event.target === dialog.current) setSelected(undefined);
        }}
      >
        <h2>{t(c.reconcileHeading)}</h2>
        <p>
          {selected?.email} · {selected?.messageId}
        </p>
        <p>{t(c.reconcileHelp)}</p>
        <form
          onSubmit={event => {
            event.preventDefault();
            setBusy(true);
            setError(undefined);
            void apiClient.emailCampaigns
              .reconcile(campaign.id, selected!.id, { status: reconcileStatus, reason })
              .then(async () => {
                setSelected(undefined);
                await client.invalidateQueries({ queryKey: queries });
              })
              .catch((failure: unknown) => setError(failure))
              .finally(() => setBusy(false));
          }}
        >
          <Field label={t(c.status)}>
            <select
              value={reconcileStatus}
              onChange={event =>
                setReconcileStatus(event.target.value as CampaignReconcileInput['status'])
              }
            >
              {(['DELIVERED', 'FAILED', 'BLACKLISTED'] as const).map(value => (
                <option key={value} value={value}>
                  {t(c.deliveryStatuses[value])}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t(c.verifiedReason)}>
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={event => setReason(event.target.value)}
            />
          </Field>
          <ErrorMessage error={error} />
          <div className="campaign-actions">
            <button
              className="campaign-primary"
              disabled={busy || reason.trim().length < 10}
              type="submit"
            >
              {t(c.apply)}
            </button>
            <button type="button" disabled={busy} onClick={() => setSelected(undefined)}>
              {t(c.close)}
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
